import { useLayoutEffect, useRef, useState } from 'react';
import { Button, Text } from '@figma/fpl-components';
import { Icon16ChevronRight } from '@figma/fpl-icons';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { CarouselArrows } from './CarouselArrows';
import { ToolCard } from './ToolCard';
import { cardSize, rowMarker, toolkitLayout } from './toolkit.stylex';
import { LOOP_COPIES, REAL_COPY, useCarousel } from './useCarousel';
import type { ToolCatalogCard } from '@/types/toolkit.types';

/** A row with fewer cards than this always fits a wide window, so it has no "Show all". */
const SHOW_ALL_MIN_CARDS = 5;
const OPEN_ANIMATION: KeyframeAnimationOptions = { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };

const styles = stylex.create({
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer3,
    minWidth: 0,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: spacer.spacer3,
    justifyContent: 'space-between',
  },
  headings: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer1,
    minWidth: 0,
  },
  text: {
    margin: 0,
  },
  showAll: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: spacer.spacer1,
  },
  chevron: {
    display: 'flex',
    transform: 'rotate(0deg)',
    transition: {
      default: 'transform 0.3s ease-out',
      '@media (prefers-reduced-motion: reduce)': 'none',
    },
  },
  chevronOpen: {
    transform: 'rotate(-90deg)',
  },
  body: {
    position: 'relative',
  },
  // Runs past the content's right edge to the edge of the page, so the next card peeks in.
  viewport: {
    marginRight: {
      default: toolkitLayout.dashboardPaddingUndo,
      '@media (max-width: 600px)': toolkitLayout.narrowPaddingUndo,
    },
    outline: {
      default: 'none',
      ':focus-visible': '1px solid currentColor',
    },
    overflowX: 'auto',
    overscrollBehaviorX: 'contain',
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  gridViewport: {
    marginRight: 0,
    overflow: 'hidden',
  },
  track: {
    display: 'flex',
    gap: toolkitLayout.cardGap,
    width: 'max-content',
  },
  // The cards keep their width, so wrapping them fills each line like the carousel.
  grid: {
    flexWrap: 'wrap',
    width: 'auto',
  },
});

interface CatalogRowProps {
  title: string;
  subtitle: string;
  cards: ToolCatalogCard[];
}

/**
 * A titled row of tool cards. Closed, it is an endless carousel when the cards do not all fit, with arrows
 * over its edges. Then "Show all" opens it into a grid of every card, and "Show less" closes it on the
 * card it was on.
 */
export function CatalogRow({ title, subtitle, cards }: CatalogRowProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { viewportRef, isLooping, move, onKeyDown } = useCarousel(cards.length, !isOpen);
  const heightBeforeToggle = useRef<number | null>(null);
  const canOpen = cards.length >= SHOW_ALL_MIN_CARDS && (isLooping || isOpen);

  const toggle = () => {
    heightBeforeToggle.current = viewportRef.current?.getBoundingClientRect().height ?? null;
    setIsOpen(!isOpen);
  };

  // An open row whose cards all fit on one line has nothing to show less of, for example after the window got
  // wider. It closes, and the closed row decides again whether it needs "Show all".
  useLayoutEffect(() => {
    const track = viewportRef.current?.firstElementChild;
    if (!isOpen || !track) {
      return;
    }
    const observer = new ResizeObserver(() => {
      const first = track.firstElementChild?.getBoundingClientRect();
      const last = track.lastElementChild?.getBoundingClientRect();
      if (first && last && first.top === last.top) {
        setIsOpen(false);
      }
    });
    observer.observe(track);
    return () => observer.disconnect();
  }, [isOpen, viewportRef]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const from = heightBeforeToggle.current;
    if (!viewport || from === null) {
      return;
    }
    heightBeforeToggle.current = null;
    if (isOpen) {
      viewport.scrollLeft = 0;
    }
    if (typeof viewport.animate !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const to = viewport.getBoundingClientRect().height;
    const animation = viewport.animate([{ height: `${from}px` }, { height: `${to}px` }], OPEN_ANIMATION);
    return () => animation.cancel();
  }, [isOpen, viewportRef]);

  const copies = isLooping ? LOOP_COPIES : [REAL_COPY];

  return (
    <section {...stylex.props(styles.row)}>
      <div {...stylex.props(styles.header, cardSize.wholeCards)}>
        <div {...stylex.props(styles.headings)}>
          <Text.h2 {...stylex.props(styles.text)} variant="heading-small" color="default">
            {title}
          </Text.h2>
          <Text.p {...stylex.props(styles.text)} variant="body-medium" color="secondary">
            {subtitle}
          </Text.p>
        </div>
        {canOpen ? (
          <Button variant="ghost" aria-expanded={isOpen} onClick={toggle}>
            <span {...stylex.props(styles.showAll)}>
              {isOpen ? t(I18N_KEYS.TOOLKIT.SHOW_LESS) : t(I18N_KEYS.TOOLKIT.SHOW_ALL)}
              <span {...stylex.props(styles.chevron, isOpen && styles.chevronOpen)} aria-hidden>
                <Icon16ChevronRight />
              </span>
            </span>
          </Button>
        ) : null}
      </div>
      <div {...stylex.props(styles.body, rowMarker)}>
        <div
          ref={viewportRef}
          {...stylex.props(styles.viewport, isOpen && styles.gridViewport)}
          role="region"
          aria-label={title}
          tabIndex={isLooping ? 0 : undefined}
          onKeyDown={isLooping ? onKeyDown : undefined}
        >
          <div {...stylex.props(styles.track, isOpen && styles.grid)}>
            {copies.flatMap((copy) =>
              cards.map((card) => (
                <ToolCard
                  key={`${copy}:${card.id}`}
                  name={card.name}
                  description={card.description}
                  thumbnail={card.thumbnail}
                  href={card.toolId ? `/tool/${card.toolId}` : undefined}
                  isCopy={copy !== REAL_COPY}
                  isToolMissing={card.isToolMissing}
                />
              )),
            )}
          </div>
        </div>
        {isLooping ? <CarouselArrows rowTitle={title} onScroll={move} bandStyle={cardSize.pictureBand} /> : null}
      </div>
    </section>
  );
}
